import type { CampaignSaveEnvelope } from './CampaignSave.js';

export interface SaveRepository {
    loadCampaign(slot: string): Promise<CampaignSaveEnvelope | null>;
    saveCampaign(slot: string, envelope: CampaignSaveEnvelope): Promise<void>;
    deleteCampaign(slot: string): Promise<void>;
    hasCampaign(slot: string): Promise<boolean>;
    listCampaignSlots(): Promise<string[]>;
}

function cloneJson<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
}

export class MemorySaveRepository implements SaveRepository {
    private readonly saves = new Map<string, CampaignSaveEnvelope>();

    async loadCampaign(slot: string): Promise<CampaignSaveEnvelope | null> {
        return this.saves.has(slot) ? cloneJson(this.saves.get(slot)!) : null;
    }

    async saveCampaign(slot: string, envelope: CampaignSaveEnvelope): Promise<void> {
        this.saves.set(slot, cloneJson(envelope));
    }

    async deleteCampaign(slot: string): Promise<void> {
        this.saves.delete(slot);
    }

    async hasCampaign(slot: string): Promise<boolean> {
        return this.saves.has(slot);
    }

    async listCampaignSlots(): Promise<string[]> {
        return Array.from(this.saves.keys());
    }
}

const DB_NAME = 'teamsquad';
const DB_VERSION = 1;
const CAMPAIGN_STORE_NAME = 'campaign_saves';

export class IndexedDbSaveRepository implements SaveRepository {
    private dbPromise: Promise<IDBDatabase> | null = null;

    async loadCampaign(slot: string): Promise<CampaignSaveEnvelope | null> {
        const value = await this.readRecord(slot);
        return value ? cloneJson(value) : null;
    }

    async saveCampaign(slot: string, envelope: CampaignSaveEnvelope): Promise<void> {
        const db = await this.getDatabase();
        await this.runRequest<void>((resolve, reject) => {
            const tx = db.transaction(CAMPAIGN_STORE_NAME, 'readwrite');
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error ?? new Error('Campaign save transaction failed.'));
            tx.objectStore(CAMPAIGN_STORE_NAME).put(cloneJson(envelope), slot);
        });
    }

    async deleteCampaign(slot: string): Promise<void> {
        const db = await this.getDatabase();
        await this.runRequest<void>((resolve, reject) => {
            const tx = db.transaction(CAMPAIGN_STORE_NAME, 'readwrite');
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error ?? new Error('Campaign delete transaction failed.'));
            tx.objectStore(CAMPAIGN_STORE_NAME).delete(slot);
        });
    }

    async hasCampaign(slot: string): Promise<boolean> {
        return (await this.readRecord(slot)) !== null;
    }

    async listCampaignSlots(): Promise<string[]> {
        const db = await this.getDatabase();
        return this.runRequest<string[]>((resolve, reject) => {
            const tx = db.transaction(CAMPAIGN_STORE_NAME, 'readonly');
            const request = tx.objectStore(CAMPAIGN_STORE_NAME).getAllKeys();
            request.onsuccess = () => resolve(request.result.map((value) => String(value)));
            request.onerror = () => reject(request.error ?? new Error('Could not list campaign slots.'));
        });
    }

    private async readRecord(slot: string): Promise<CampaignSaveEnvelope | null> {
        const db = await this.getDatabase();
        return this.runRequest<CampaignSaveEnvelope | null>((resolve, reject) => {
            const tx = db.transaction(CAMPAIGN_STORE_NAME, 'readonly');
            const request = tx.objectStore(CAMPAIGN_STORE_NAME).get(slot);
            request.onsuccess = () => resolve((request.result as CampaignSaveEnvelope | undefined) ?? null);
            request.onerror = () => reject(request.error ?? new Error('Could not load campaign save.'));
        });
    }

    private async getDatabase(): Promise<IDBDatabase> {
        if (this.dbPromise) {
            return this.dbPromise;
        }
        if (typeof indexedDB === 'undefined') {
            throw new Error('IndexedDB is unavailable in this environment.');
        }
        this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(CAMPAIGN_STORE_NAME)) {
                    db.createObjectStore(CAMPAIGN_STORE_NAME);
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error ?? new Error('Could not open campaign save database.'));
        });
        return this.dbPromise;
    }

    private runRequest<T>(executor: (resolve: (value: T) => void, reject: (reason?: unknown) => void) => void): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            executor(resolve, reject);
        });
    }
}

let sharedRepository: SaveRepository | null = null;

export function getCampaignSaveRepository(): SaveRepository {
    if (!sharedRepository) {
        sharedRepository = typeof indexedDB === 'undefined'
            ? new MemorySaveRepository()
            : new IndexedDbSaveRepository();
    }
    return sharedRepository;
}

export function setCampaignSaveRepository(repository: SaveRepository | null): void {
    sharedRepository = repository;
}
